import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { DeleteOutline as ArchiveIcon, LabelOutlined as LabelIcon } from '@mui/icons-material';
import ZoneFormDialog from '../../components/zones/ZoneFormDialog';
import ZonesMap from '../../components/zones/ZonesMap';
import LabelChips from '../../components/common/LabelChips';
import LoadingSkeleton from '../../components/LoadingSkeleton';
import PageHeader from '../../components/PageHeader';
import { APP_SHAPE, elevatedPanelSx } from '../../constants/shape';
import { useAuthPermissions } from '../../hooks/useAuthPermissions';
import { useConfirm } from '../../hooks/useConfirm';
import { useOrganizationStore } from '../../stores/organization.store';
import { useZoneStore } from '../../stores/zone.store';

export default function Zones() {
  const { t } = useTranslation();
  const selectedOrganization = useOrganizationStore((state) => state.selectedOrganization);
  const {
    zones,
    isLoading,
    isSaving,
    error,
    clearError,
    selectedZoneDetails,
    selectedZoneLoading,
    fetchZones,
    fetchZoneCollectionPoints,
    createZone,
    archiveZone,
  } = useZoneStore();
  const { canMutate } = useAuthPermissions();
  const { confirm, confirmDialog } = useConfirm();

  const [selectedZoneId, setSelectedZoneId] = useState<string | null>(null);
  const [draftBoundary, setDraftBoundary] = useState<number[][] | null>(null);

  const orgId = selectedOrganization?.id;
  const canCreate = Boolean(orgId && canMutate(`recyclee.${orgId}.*.zone.create`));
  const canArchive = Boolean(orgId && canMutate(`recyclee.${orgId}.*.zone.delete`));

  useEffect(() => {
    if (orgId) void fetchZones({ organization_id: orgId, limit: 100 });
  }, [orgId, fetchZones]);

  useEffect(() => {
    if (orgId && selectedZoneId) void fetchZoneCollectionPoints(orgId, selectedZoneId);
  }, [orgId, selectedZoneId, fetchZoneCollectionPoints]);

  if (!selectedOrganization) {
    return <Alert severity="info">{t('zones.selectOrganization')}</Alert>;
  }

  const handleBoundaryDrafted = (boundary: number[][]) => {
    clearError();
    setDraftBoundary(boundary);
  };

  const submitZone = async (name: string, color: string) => {
    if (!draftBoundary || !orgId) return false;
    const result = await createZone(orgId, { name, color, boundary: draftBoundary });
    if (result) setSelectedZoneId(result.id);
    return Boolean(result);
  };

  const archive = async (zoneId: string, name: string) => {
    if (!orgId) return;
    const confirmed = await confirm({
      title: t('common.archive'),
      description: t('zones.archiveConfirm', { name }),
      tone: 'danger',
    });
    if (!confirmed) return;
    const success = await archiveZone(orgId, zoneId);
    if (success && selectedZoneId === zoneId) setSelectedZoneId(null);
  };

  return (
    <Box>
      <PageHeader
        icon={<LabelIcon />}
        title={t('zones.title')}
        subtitle={t('zones.subtitle')}
        sx={{ borderRadius: `${APP_SHAPE.radiusLg}px` }}
      />

      {error && <Alert severity="error" onClose={clearError} sx={{ mb: 2 }}>{error}</Alert>}
      {!canCreate && <Alert severity="info" sx={{ mb: 2 }}>{t('zones.readOnlyHint')}</Alert>}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) 320px' }, gap: 3, alignItems: 'stretch' }}>
        <ZonesMap
          zones={zones}
          selectedZoneId={selectedZoneId}
          onSelectZone={setSelectedZoneId}
          onBoundaryDrafted={canCreate ? handleBoundaryDrafted : () => {}}
        />

        <Paper
          variant="outlined"
          sx={{
            borderRadius: `${APP_SHAPE.radiusLg}px`,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            minHeight: 420,
          }}
        >
          <Box sx={{ p: 2, flexShrink: 0 }}>
            <Typography variant="subtitle1" fontWeight={600}>{t('zones.listTitle')}</Typography>
          </Box>
          <Divider />
          {isLoading ? (
            <Box sx={{ p: 2 }}><LoadingSkeleton variant="table" filters={false} columns={1} rows={4} /></Box>
          ) : zones.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>{t('zones.emptyHint')}</Typography>
          ) : (
            <List dense disablePadding sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
              {zones.map((zone) => (
                <ListItemButton
                  key={zone.id}
                  selected={zone.id === selectedZoneId}
                  onClick={() => setSelectedZoneId(zone.id)}
                  sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}
                >
                  <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: zone.color, flexShrink: 0 }} />
                  <ListItemText
                    primary={zone.name}
                    secondary={t('zones.pointCount', { count: zone.collection_points_count })}
                  />
                  {canArchive && (
                    <Tooltip title={t('suppliers.table.archive')}>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={(event) => { event.stopPropagation(); void archive(zone.id, zone.name); }}
                      >
                        <ArchiveIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </ListItemButton>
              ))}
            </List>
          )}
        </Paper>
      </Box>

      {selectedZoneId && (
        <Paper elevation={2} sx={{ ...elevatedPanelSx, mt: 3 }}>
          <Box sx={{ p: 2 }}>
            <Typography variant="subtitle1" fontWeight={600}>
              {selectedZoneDetails?.zone.name ?? ''}
            </Typography>
          </Box>
          {selectedZoneLoading ? (
            <Box sx={{ p: 2 }}><LoadingSkeleton variant="table" filters={false} columns={4} rows={3} /></Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('suppliers.collectionPoints.table.name')}</TableCell>
                    <TableCell>{t('suppliers.collectionPoints.table.address')}</TableCell>
                    <TableCell>{t('suppliers.collectionPoints.table.contact')}</TableCell>
                    <TableCell>{t('suppliers.collectionPoints.table.labels')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(selectedZoneDetails?.collection_points ?? []).map((point) => (
                    <TableRow key={point.id} hover>
                      <TableCell><Typography variant="body2" fontWeight={600}>{point.name}</Typography></TableCell>
                      <TableCell>{point.address || '—'}</TableCell>
                      <TableCell>{point.contact_name || point.contact_phone || '—'}</TableCell>
                      <TableCell><LabelChips labels={point.labels} /></TableCell>
                    </TableRow>
                  ))}
                  {(selectedZoneDetails?.collection_points ?? []).length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} sx={{ py: 4, textAlign: 'center' }}>
                        <Stack alignItems="center" spacing={1}>
                          <Typography variant="body2" color="text.secondary">{t('zones.noPointsInZone')}</Typography>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      )}

      <ZoneFormDialog
        open={draftBoundary != null}
        saving={isSaving}
        submitError={draftBoundary ? error : null}
        onClose={() => setDraftBoundary(null)}
        onSubmit={submitZone}
      />
      {confirmDialog}
    </Box>
  );
}
